import React, { useCallback, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { BypassLayout } from '../components/common';
import StudentTablePanel from '../components/StudentsHub/StudentTablePanel';
import StudentLearningPanel from '../components/StudentsHub/StudentLearningPanel';
import AssignmentFeedPanel from '../components/StudentsHub/AssignmentFeedPanel';

/**
 * 「학습」 통합 페이지 (/students-hub)
 *
 * 구조: 좌측 = 페이지네이션 학생 목록(마스터) / 우측 = 선택 학생 학습 화면(디테일)
 * - 상태 A(미선택): 좌측 테이블 full-width
 * - 상태 B(선택, 데스크톱): 좌측 테이블 폭 축소(compact) + 우측 학습 패널
 * - 모바일(선택): 목록 ↔ 패널 풀스크린 토글
 *
 * 우측 패널은 StudentLearningPanel(카이테스트 본체 공유 컴포넌트, 매스컨셉/문제).
 * key={studentId} 로 학생 전환 시 remount → stale 상태 방지.
 *
 * 「전체 테스트」(view='tests')는 **같은 자리에서 표만 갈린다**(오너 결정 2026-09-06).
 * 학생 목록 대신 배정 목록이 서고, 오른쪽 학습 화면은 열리지 않는다 — 그 표의 행은 학생이
 * 아니라 배정 한 건을 가리키고, 누르면 채점 창이 이 화면 위에 뜬다.
 * 「학생 / 테스트」를 오가는 버튼 쌍은 두지 않는다. 들어가는 길은 학생 목록 위의 [전체 테스트]
 * 하나뿐이고, 나오는 길은 뒤로가기와 사이드바의 「학습」이다 — 그래서 주소로 가른다.
 *
 * 상세 계획: docs/students-hub/students-hub-integration-plan.md ·
 *            docs/2026-09-06-students-hub-assignment-list.html
 */
function StudentsHub({ view = 'students' }) {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const isTests = view === 'tests';
  const isSelected = !isTests && !!studentId;
  const [ratingRevision, setRatingRevision] = useState(0);
  const handleLearningChanged = useCallback(() => setRatingRevision((value) => value + 1), []);

  const handleSelectStudent = (student) => {
    navigate(`/students-hub/student/${student.student_id}`);
  };

  const handleCloseDetail = () => {
    navigate('/students-hub');
  };

  return (
    <BypassLayout
      renderContent={({ isMobileView }) => {
        // 모바일: 선택 시 패널 풀스크린, 아니면 목록 (단일 컬럼 토글)
        if (isMobileView) {
          return (
            <div
              className="flex flex-1 flex-col"
              style={{ width: 'calc(100% - 28.8px)', height: 'calc(100% - 28.8px)', margin: '14.4px' }}
            >
              {isTests ? (
                <AssignmentFeedPanel />
              ) : isSelected ? (
                <StudentLearningPanel
                  key={studentId}
                  studentId={studentId}
                  variant="embedded"
                  onClose={handleCloseDetail}
                  onLearningChanged={handleLearningChanged}
                />
              ) : (
                <StudentTablePanel selectedStudentId={null} onSelectStudent={handleSelectStudent} ratingRevision={ratingRevision} />
              )}
            </div>
          );
        }

        // 데스크톱: 선택 시 좌우 분할(좌 440~520px + 우 패널), 미선택 시 좌측 full-width.
        // 테스트 목록에서는 분할이 없다 — 행이 학생을 고르지 않으므로 오른쪽이 열릴 일이 없다.
        const split = isSelected;
        if (isTests) {
          return (
            <div className="flex h-[calc(100vh-6rem)] w-full gap-4">
              <div className="size-full min-w-0 flex-1">
                <AssignmentFeedPanel />
              </div>
            </div>
          );
        }
        return (
          <div className="flex h-[calc(100vh-6rem)] w-full gap-4">
            {/* 좌측: 학생 목록 (마스터) */}
            {/* 학생 선택 후에도 레이팅 열을 유지하도록 목록을 440~520px로 넓힌다. */}
            <div
              className={split ? 'h-full shrink-0' : 'h-full w-full min-w-0 flex-1'}
              style={split ? { width: 'clamp(440px, 40%, 520px)', transition: 'width 300ms ease' } : { transition: 'width 300ms ease' }}
            >
              <StudentTablePanel
                compact={split}
                selectedStudentId={studentId ? parseInt(studentId, 10) : null}
                onSelectStudent={handleSelectStudent}
                ratingRevision={ratingRevision}
              />
            </div>

            {/* 우측: 학습 화면 (디테일) */}
            {split && (
              <div className="h-full min-w-0 flex-1">
                <StudentLearningPanel
                  key={studentId}
                  studentId={studentId}
                  variant="embedded"
                  onClose={handleCloseDetail}
                  onLearningChanged={handleLearningChanged}
                />
              </div>
            )}
          </div>
        );
      }}
    />
  );
}

export default StudentsHub;
